import AccessTime from '@mui/icons-material/AccessTime'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CancelIcon from '@mui/icons-material/Cancel'
import VideocamIcon from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Button } from '@mui/material'
import dayjs from 'dayjs'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { RectangleRadioButton } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { MOMENT_FORMATS, BOOKED_EVENT_MODES, displayNamesOfEventTypes } from 'src/consumer/constants'
import { getValidNumberFromStringNumber } from 'src/consumer/helpers/utilFunctions'
import { getBookedSlotDetails } from 'src/mentor/services/bookedSlotCache'
import { usePromiseDialog } from 'src/customHooks/usePromiseDialog'
import DayjsDatePicker from 'src/mentor/components/commonReportComponents/components/datePicker/DayjsDatePicker'
import {
  calculateNextVisitDaysFromDateDayjs,
  calculateNextVistDateInMoment,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { PMPSectionInputBox, PMPSectionInputLabel } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { getNextVisitDateDifference } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/utils/nextVisitRosterCalendarUtils'
import NextVisitSlotSelector from './NextVisitSlotSelector'

const EMRNextVisitComponent = forwardRef(
  (
    {
      nextVisitInputType,
      setNextVisitInputType,
      nextVisitInputValue,
      setNextVisitInputValue,
      bookedEventInfoStartTime,
      nextVisitBookedEventId,
      showHoldSlotForNextVisit = false,
      currentClinicLocation,
      mentorProfileData,
      roomId,
      currentEventTypeId,
      currentPatient,
      refreshCurrentBookedEventInfoData,
      currentFee,
      currentCurrency,
      currentBookedEventId,
      saveReport,
      nextVisitHoldSlotOmissionReason,
      setNextVisitHoldSlotOmissionReason,
      isMarkingAsCompleted,
      leaveRoom,
    },
    componentRef,
  ) => {
    const [calendarVisible, setCalendarVisible] = useState(false)
    const [slotSelectorOpen, setSlotSelectorOpen] = useState(false)
    const [bookedSlot, setBookedSlot] = useState(null)
    const [loading, setLoading] = useState(false)
    const holdSlotOmissionDialog = usePromiseDialog()
    const promiseResolverRef = useRef(null)

    const selectedDate = useMemo(() => {
      const calculatedDate = calculateNextVistDateInMoment(
        nextVisitInputValue, // Default to 7 days if no value
        nextVisitInputType,
        dayjs.utc(bookedEventInfoStartTime).local(),
      )

      return dayjs(calculatedDate)
    }, [nextVisitInputValue, nextVisitInputType, bookedEventInfoStartTime])
    
    // Remove console.log that was causing continuous logging
    // const currentEventTypeObject = useMemo(() => {
    //   mentorProfileData?.eventTypes?.find((eventType) => eventType?.uid === currentEventTypeId)
    // }, [mentorProfileData, currentEventTypeId])
    const currentEventTypeObject = useMemo(() => {
      return mentorProfileData?.eventTypes?.find(
        (eventType) => eventType?.uid === currentEventTypeId,
      )
    }, [mentorProfileData, currentEventTypeId])

    const showBookSpecificTimeButton =
      nextVisitInputValue > 0 && showHoldSlotForNextVisit && !nextVisitBookedEventId

    const todayDate = new Date()
    const maxDate = new Date()
    maxDate?.setMonth(todayDate?.getMonth() + 12)

    const dayjsMinDateInLocalTimezone = dayjs.utc(bookedEventInfoStartTime).local()
    const minDate = dayjsMinDateInLocalTimezone.toDate()

    minDate.setHours(0, 0, 0, 0)

    const handleSlotSelection = (slotData) => {
      setBookedSlot(slotData)
      // Calculate days from booked event start time to selected slot
      const selectedDateTime = dayjs(slotData.startDateTime || slotData.startTime)
      setSelectedNextVisitDate(selectedDateTime.startOf('day'))
      // // const differenceInDays = selectedDateTime.diff(eventStartTime, 'day')
      // const differenceInDays = calculateNextVisitDaysFromDateDayjs(
      //   selectedDateTime.format(MOMENT_FORMATS.formatThatServerSends),
      //   eventStartTime,
      // )

      // // Update the next visit values to reflect the exact booking
      // setNextVisitInputType('days')
      // setNextVisitInputValue(differenceInDays)
    }

    function resetNextVisit() {
      setBookedSlot(null)
      setNextVisitInputValue('')
      setNextVisitInputType('days')
    }

    function setSelectedNextVisitDate(nextValue) {
      const differenceInDays = calculateNextVisitDaysFromDateDayjs(
        dayjs(nextValue).format(MOMENT_FORMATS.formatThatServerSends),
        dayjs.utc(bookedEventInfoStartTime).local(),
      )

      setNextVisitInputType('days')
      setNextVisitInputValue(differenceInDays)
      if (showHoldSlotForNextVisit) {
        setSlotSelectorOpen(true)
      }
    }

    useEffect(() => {
      if (!nextVisitBookedEventId) return
      let cancelled = false
      setLoading(true)
      getBookedSlotDetails(nextVisitBookedEventId).then((slot) => {
        if (cancelled) return
        if (slot) setBookedSlot(slot)
        setLoading(false)
      })
      return () => {
        cancelled = true
      }
    }, [nextVisitBookedEventId])

    useImperativeHandle(componentRef, () => ({
      /**
       * Opens the hold slot omission reason dialog and returns a promise
       * @returns {Promise} Resolves when user provides reason, rejects if cancelled
       */
      confirmOmissionReason: () => {
        return holdSlotOmissionDialog.show()
      },
    }))

    return (
      <>
        <div>
          <PMPSectionInputLabel main className="d-flex gap-2 align-items-center">
            <div>Next Visit</div>
            <CancelIcon
              style={{
                color: 'red',
                fontSize: '16px',
                cursor: bookedSlot ? 'not-allowed' : 'pointer',
              }}
              onClick={() => {
                if (!!bookedSlot) {
                  return
                }
                resetNextVisit()
                setSlotSelectorOpen(false)
              }}
            />
          </PMPSectionInputLabel>

          {/* Show booked slot info if available */}
          {bookedSlot && !loading && (
            <div
              style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: bookedSlot?.status === 'booked' ? '#e8f5e8' : '#fff8e1',
                borderRadius: '8px',
                border: `1.5px solid ${bookedSlot?.status === 'booked' ? '#4caf50' : '#ffc107'}`,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Header with Status */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: bookedSlot?.status === 'booked' ? '#2e7d32' : '#ff8f00',
                        marginBottom: '8px',
                      }}
                    >
                      {bookedSlot?.status === 'booked' ? '✅' : '⏳'} Next Visit Slot{' '}
                      {bookedSlot?.status === 'booked' ? 'Booked' : 'Blocked'}
                    </div>
                    
                    {/* Time Slot */}
                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
                      {bookedSlot.timeSlot || (() => {
                        try {
                          const start = dayjs(bookedSlot.startTime)
                          const end = bookedSlot.endTime ? dayjs(bookedSlot.endTime) : start.add(30, 'minutes')
                          return `${start.format('h:mm A')} - ${end.format('h:mm A')}`
                        } catch {
                          return 'Time not available'
                        }
                      })()}
                    </div>
                    
                    {/* Date and Relative Time */}
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      {dayjs(bookedSlot.startTime).format('dddd, MMMM D, YYYY')} (
                      {getNextVisitDateDifference(dayjs(bookedSlot.startTime), dayjs(minDate))})
                    </div>
                  </div>
                </div>

                {/* Details Section */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {/* Event Type */}
                  {bookedSlot?.eventType && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <CalendarMonthIcon style={{ fontSize: '16px', color: '#666' }} />
                      <span style={{ color: '#666' }}>Event Type:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {displayNamesOfEventTypes?.[bookedSlot.eventType] || bookedSlot.eventType}
                      </span>
                    </div>
                  )}

                  {/* Meeting Mode (Online/Offline) */}
                  {bookedSlot?.meetingSrc && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      {bookedSlot.meetingSrc === 'aumhum' || bookedSlot.meetingSrc === 'online' ? (
                        <VideocamIcon style={{ fontSize: '16px', color: '#1976d2' }} />
                      ) : (
                        <LocationOnIcon style={{ fontSize: '16px', color: '#d32f2f' }} />
                      )}
                      <span style={{ color: '#666' }}>Mode:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {BOOKED_EVENT_MODES?.[bookedSlot.meetingSrc]?.title || 
                         (bookedSlot.meetingSrc === 'aumhum' || bookedSlot.meetingSrc === 'online' ? 'Online' : 'Offline')}
                      </span>
                    </div>
                  )}

                  {/* Chamber Room (for offline sessions) */}
                  {bookedSlot?.chamberRoomName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <LocationOnIcon style={{ fontSize: '16px', color: '#d32f2f' }} />
                      <span style={{ color: '#666' }}>Location:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {bookedSlot.chamberRoomName}
                      </span>
                    </div>
                  )}

                  {/* Duration */}
                  {bookedSlot?.startTime && bookedSlot?.endTime && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <AccessTime style={{ fontSize: '16px', color: '#666' }} />
                      <span style={{ color: '#666' }}>Duration:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {(() => {
                          try {
                            const start = dayjs(bookedSlot.startTime)
                            const end = dayjs(bookedSlot.endTime)
                            const durationMinutes = end.diff(start, 'minute')
                            if (durationMinutes < 60) {
                              return `${durationMinutes} minutes`
                            }
                            const hours = Math.floor(durationMinutes / 60)
                            const minutes = durationMinutes % 60
                            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hour${hours > 1 ? 's' : ''}`
                          } catch {
                            return 'N/A'
                          }
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Fee Information (if available) */}
                  {bookedSlot?.feePaid && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#666' }}>Fee:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {bookedSlot.currency?.toUpperCase() || 'INR'} {bookedSlot.feePaid}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons Section - Reserved for future actions */}
                {/* 
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // TODO: Handle modify action
                    }}
                  >
                    Modify
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => {
                      // TODO: Handle cancel action
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={() => {
                      // TODO: Handle change event type action
                    }}
                  >
                    Change Mode
                  </Button>
                </div>
                */}
              </div>
            </div>
          )}
          {!(bookedSlot && !loading) && (
            <>
              <div className="d-flex" style={{ gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ width: '120px' }}>
                  <PMPSectionInputBox
                    className="pmpTextarea-focus:focus"
                    placeholder={`Enter number`}
                    type="number"
                    disabled={!!bookedSlot ? true : false}
                    style={{ 
                      background: '#fff',
                      textAlign: 'center',
                      fontWeight: 500,
                      fontSize: '15px',
                    }}
                    value={nextVisitInputValue === null || nextVisitInputValue === undefined || nextVisitInputValue === 0 ? '' : nextVisitInputValue}
                    onChange={(e) => {
                      const inputValue = e.target.value
                      
                      // Allow empty string for clearing the field
                      if (inputValue === '' || inputValue === null) {
                        setNextVisitInputValue('')
                        return
                      }
                      
                      // Handle negative sign or invalid characters early
                      if (inputValue === '-' || inputValue === '.' || inputValue === '+' || inputValue === 'e' || inputValue === 'E') {
                        return
                      }
                      
                      // Get valid number
                      const numValue = getValidNumberFromStringNumber(inputValue)
                      
                      // Only update if it's a valid positive number or empty
                      if (numValue !== null && numValue > 0) {
                        // Set reasonable max limits based on input type
                        const maxValue = nextVisitInputType === 'days' ? 365 : nextVisitInputType === 'weeks' ? 52 : 12
                        if (numValue <= maxValue) {
                          setNextVisitInputValue(numValue)
                        }
                      } else if (inputValue === '') {
                        setNextVisitInputValue('')
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent 'e', 'E', '+', '-' keys
                      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                        e.preventDefault()
                      }
                    }}
                    min="1"
                    max={nextVisitInputType === 'days' ? 365 : nextVisitInputType === 'weeks' ? 52 : 12}
                    step="1"
                  />
                </div>
                <div style={{ width: '200px' }}>
                  <RectangleRadioButton
                    disabled={!!bookedSlot ? true : false}
                    options={[
                      { label: 'Days', id: 'days' },
                      { label: 'Weeks', id: 'weeks' },
                      { label: 'Months', id: 'months' },
                    ]}
                    selectedId={nextVisitInputType}
                    selectionFunction={(id) => {
                      setNextVisitInputType(id)
                    }}
                  />
                </div>
                <div className="d-flex align-items-start gap-2">
                  <div>or</div>
                  <DayjsDatePicker
                    date={calculateNextVistDateInMoment(
                      nextVisitInputValue,
                      nextVisitInputType,
                      dayjs.utc(bookedEventInfoStartTime),
                    ).local()}
                    calendarProps={{
                      minDate: dayjs().toDate(),
                      maxDate: maxDate,
                      minDetail: 'decade',
                      showNeighbouringMonth: false,
                      locale: sessionStorage.getItem('languageCode') || 'en',
                    }}
                    format="DD MMM YYYY, dddd"
                    buttonProps={{
                      variant: 'outlined',
                      size: 'small',
                      disabled: !!bookedSlot ? true : false,
                      startIcon: <CalendarMonthIcon />,
                      sx: {
                        background: '#fff',
                        textTransform: 'none',
                        border: '1px solid #cccccc',
                      },
                      // disableRipple: true,
                    }}
                    extraHandlingAfterOnClick={() => {
                      if (
                        (nextVisitInputValue === null || nextVisitInputValue === undefined) &&
                        !calendarVisible
                      ) {
                        setNextVisitInputValue(7)
                      }
                    }}
                    onChange={setSelectedNextVisitDate}
                    placeholder="Choose Next Visit Date"
                  />
                </div>
              </div>

              {/* Book Specific Time Button */}
              {showBookSpecificTimeButton && !bookedSlot && (
                <div style={{ marginTop: '16px' }}>
                  <Button
                    variant="outlined"
                    background="emrPrimary"
                    startIcon={<AccessTime />}
                    onClick={() => setSlotSelectorOpen(true)}
                    style={{
                      textTransform: 'none',
                      backgroundColor: bookedSlot ? '#e3f2fd' : '#fff',
                    }}
                  >
                    {bookedSlot ? 'Change Specific Time' : 'Hold Slot For Next Visit'}
                  </Button>

                  {!bookedSlot && nextVisitInputValue && nextVisitInputType && (
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        marginTop: '8px',
                        fontStyle: 'italic',
                      }}
                    >
                      💡 Tip: Hold a slot for a specific time slot for the date:{' '}
                      {calculateNextVistDateInMoment(
                        nextVisitInputValue,
                        nextVisitInputType,
                        dayjs.utc(bookedEventInfoStartTime),
                      )
                        .local()
                        .format('DD/MM/YYYY, dddd')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Slot Selector Modal */}
          <NextVisitSlotSelector
            open={slotSelectorOpen}
            setOpen={setSlotSelectorOpen}
            leaveRoom={leaveRoom}
            bookingDate={dayjsMinDateInLocalTimezone}
            onClose={(willNotResetNextVisit = false) => {
              setSlotSelectorOpen(false)
              if (!willNotResetNextVisit) {
                resetNextVisit()
              }
            }}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedNextVisitDate}
            onSlotSelect={handleSlotSelection}
            selectedItem={currentClinicLocation}
            roomId={roomId}
            mentorProfile={mentorProfileData}
            currentEventTypeObject={currentEventTypeObject}
            currentFee={currentFee}
            selectedPatient={currentPatient}
            refreshData={refreshCurrentBookedEventInfoData}
            currentBookedEventId={currentBookedEventId}
            currentCurrency={currentCurrency}
            saveReport={saveReport}
            nextVisitHoldSlotOmissionReason={nextVisitHoldSlotOmissionReason}
            setNextVisitHoldSlotOmissionReason={setNextVisitHoldSlotOmissionReason}
            isMarkingAsCompleted={isMarkingAsCompleted}
            holdSlotOmissionDialog={holdSlotOmissionDialog}
          />
        </div>
      </>
    )
  },
)

export default EMRNextVisitComponent
